import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import qs from 'qs';
import { CompositeMatchingResponse } from 'src/types/compositeMatching';
import { config } from 'src/lib/config';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';

// TODO: Once composite matching is deployed to the standard content-query prod URL,
// delete this override and call `${config.services.designConductorServiceApi.prefixUrl}`
// (use DesignConductorService) instead.
const TEMP_COMPOSITE_BASE_URL = 'https://gallery-content-query-composite.gallery.vpsvc.com/';

export interface CompositeMatchingParams {
    productKey: string;
    productVersion: number;
    culture: string;
    productOptions: Record<string, string>;
    documentUrl?: string;
    designPersonalizationContextId?: string;
    designConceptId?: string;
    brandId?: string;
    sourceProductKey?: string;
    sourceProductVersion?: number;
    sourceProductOptions?: Record<string, string>;
}

export interface CompositeMatchingRequest {
    baseUrl: string;
    pathWithQuery: string;
    url: string;
}

export const buildCompositeMatchingRequest = (params: CompositeMatchingParams): CompositeMatchingRequest => {
    const query = {
        runAllMatchers: true,
        noCache: true,
        debug: true,
        previewWidth: 824,
        requestor: config.appName,
        documentUrl: params.documentUrl,
        designPersonalizationContextId: params.designPersonalizationContextId,
        designConceptId: params.designConceptId,
        brandId: params.brandId,
        sourceProductKey: params.sourceProductKey,
        sourceProductVersion: params.sourceProductVersion,
        sourceProductOptions: params.sourceProductOptions,
        productOptions: params.productOptions,
    };

    const path = `api/v2/Product/${encodeURIComponent(params.productKey)}`
        + `/version/${params.productVersion}`
        + `/Culture/${encodeURIComponent(params.culture)}/compositeMatching`;
    const pathWithQuery = `${path}?${qs.stringify(query)}`;

    return {
        baseUrl: TEMP_COMPOSITE_BASE_URL,
        pathWithQuery,
        url: `${TEMP_COMPOSITE_BASE_URL}${pathWithQuery}`,
    };
};

export const useCompositeMatching = (
    params: CompositeMatchingParams | null,
): UseQueryResult<CompositeMatchingResponse, Error> => {
    const accessToken = useRecoilValue(isAuthorizedQuery);

    return useQuery<CompositeMatchingResponse, Error>(
        [
            'compositeMatching',
            params?.productKey,
            params?.productVersion,
            params?.culture,
            params?.documentUrl,
            params?.designPersonalizationContextId,
            params?.designConceptId,
            params?.brandId,
            params?.productOptions,
            params?.sourceProductKey,
            params?.sourceProductVersion,
            params?.sourceProductOptions,
        ],
        async () => {
            if (!params) throw new Error('Missing composite matching params');
            if (!accessToken) throw new Error('Missing access token');

            const { url } = buildCompositeMatchingRequest(params);

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Composite matching failed: ${response.status} ${response.statusText}`);
            }

            return response.json() as Promise<CompositeMatchingResponse>;
        },
        {
            enabled: !!params && !!accessToken,
            refetchOnWindowFocus: false,
            retry: false,
            suspense: false,
        },
    );
};
